// M1.6 — header bell-icon dropdown surfacing Brain flags.
// Polls every 30s for new flags (cheap; future M1.x: realtime onValue listener).

import { useEffect, useRef, useState } from 'react';
import { Brain } from '@/sdk/brain';
import type { Flag } from '@/sdk/brain';

function severityColor(severity: Flag['severity']): string {
  switch (severity) {
    case 'urgent':
      return 'text-orange';
    case 'attention':
      return 'text-amber';
    default:
      return 'text-text-mute';
  }
}

function severityIcon(severity: Flag['severity']): string {
  switch (severity) {
    case 'urgent':
      return '🚨';
    case 'attention':
      return '⚠';
    default:
      return 'ℹ';
  }
}

function fmtAgo(iso: string): string {
  const diffSec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diffSec < 60) return `${Math.round(diffSec)}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  return `${Math.round(diffSec / 86400)}d ago`;
}

export function FlagsBell() {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  async function refresh() {
    try {
      const fs = await Brain.flag.list();
      setFlags(fs);
    } catch {
      // Silently — bell shouldn't break the page if flags aren't loadable
    }
  }

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, []);

  // Close on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!dropdownRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const count = flags.length;
  const hasUrgent = flags.some((f) => f.severity === 'urgent');

  async function act(id: string, kind: 'ack' | 'dismiss') {
    setBusy(true);
    try {
      if (kind === 'ack') await Brain.flag.acknowledge(id);
      else await Brain.flag.dismiss(id);
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative text-xs px-2 py-1 rounded bg-paper/10 hover:bg-paper/20 border border-paper/20 text-white"
        aria-label={`${count} pending flag${count === 1 ? '' : 's'}`}
      >
        🔔
        {count > 0 && (
          <span
            className={`absolute -top-1 -right-1 ${hasUrgent ? 'bg-orange' : 'bg-amber'} text-white text-[10px] font-bold rounded-full px-1 min-w-[16px] text-center`}
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-96 max-h-[500px] bg-paper text-text border border-line-dark rounded shadow-lg z-50 flex flex-col">
          <header className="px-3 py-2 border-b border-line flex items-center justify-between">
            <span className="font-semibold text-sm">
              Pending flags {count > 0 && <span className="text-text-mute">({count})</span>}
            </span>
            <button
              onClick={() => void refresh()}
              className="text-xs text-text-mute hover:text-orange"
            >
              ↻ Refresh
            </button>
          </header>

          <div className="flex-1 overflow-y-auto">
            {flags.length === 0 ? (
              <div className="text-sm text-text-mute italic text-center py-6">
                No pending flags. ✓
              </div>
            ) : (
              <ul>
                {flags.map((f) => (
                  <li key={f.id} className="border-b border-line p-3 text-sm last:border-b-0">
                    <div className="flex items-start gap-2">
                      <span className={`text-lg ${severityColor(f.severity)}`}>
                        {severityIcon(f.severity)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{f.title}</div>
                        <div className="text-xs text-text-mute mt-0.5">
                          {f.category} · {fmtAgo(f.createdAt)}
                        </div>
                        <div className="text-xs mt-2 whitespace-pre-wrap">{f.body}</div>
                        {f.suggestedAction && (
                          <div className="text-xs mt-2 italic text-text-mute">
                            → {f.suggestedAction}
                          </div>
                        )}
                        <div className="flex gap-1 mt-2 text-xs">
                          <button
                            onClick={() => act(f.id, 'ack')}
                            disabled={busy}
                            className="px-2 py-0.5 bg-bg hover:bg-line rounded"
                          >
                            Acknowledge
                          </button>
                          <button
                            onClick={() => act(f.id, 'dismiss')}
                            disabled={busy}
                            className="px-2 py-0.5 bg-bg hover:bg-orange/20 hover:text-orange rounded"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
